import { useRef, useState } from "react";
import { useClickOutside } from "shared/model";
import { ChevronIcon } from "../icons";
import { SelectList } from "./SelectList";
import { useListPlacement } from "./useListPlacement";
import { useSelectKeyboard } from "./useSelectKeyboard";

import styles from "./Select.module.css";

export interface SelectOption<Value extends string> {
  value: Value;
  label: string;
}

interface Props<Value extends string> {
  options: SelectOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  placeholder?: string;
  disabled?: boolean;
  // Классы кнопки-триггера, чтобы она выглядела как поле формы.
  className?: string;
  labelledBy?: string;
}

// Свой выпадающий список вместо <select>: нативный список
// не даёт стилизовать скроллбар во всех браузерах.
export const Select = <Value extends string>({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className = "",
  labelledBy,
}: Props<Value>) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const { placement, measure } = useListPlacement(triggerRef);

  useClickOutside(rootRef, () => setIsOpen(false), isOpen);

  const close = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const open = () => {
    measure();
    setIsOpen(true);
  };

  const handleKeyDown = useSelectKeyboard({ isOpen, listRef, open, close });

  const current = options.find((option) => option.value === value);

  return (
    <div className={styles.root} ref={rootRef} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${className}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={labelledBy}
        onClick={isOpen ? () => setIsOpen(false) : open}
      >
        <span className={current ? "" : styles.placeholder}>
          {current?.label ?? placeholder}
        </span>
        <ChevronIcon
          direction={isOpen ? "up" : "down"}
          fill="currentColor"
          width={10}
          height={5}
        />
      </button>

      {isOpen && (
        <SelectList
          ref={listRef}
          options={options}
          value={value}
          placement={placement}
          onSelect={(index) => {
            onChange(options[index].value);
            close();
          }}
        />
      )}
    </div>
  );
};
